import { Injectable } from '@angular/core';
import { ConfigData } from '@osapp/model';
import { VersionsService } from '@osapp/modules/versions/services/versions.service';
import { PlatformService } from '@osapp/services';
import { CapacitorPluginSce, SCEDAInterface, StatusSECPlugin } from 'capacitor-plugin-sce';
import { BehaviorSubject, Subject } from 'rxjs';
import { EDeviceType } from '../shared/enums/EDeviceType';
import { SceStatus } from './sce-status';
import { SceStatusType } from './sce-status-type';


@Injectable({
  providedIn: 'root',
})
export class SceEventService implements SCEDAInterface {
  	private _sce_event$ = new BehaviorSubject<any>(null);
  	public sce_event$ = this._sce_event$.asObservable();

	private _sce_log$ = new BehaviorSubject<any>(null);
  	public sce_log$ = this._sce_log$.asObservable();

	private _sceStatus$ = new BehaviorSubject<SceStatus>({ status: 'idle' });
	public readonly sceStatus$ = this._sceStatus$.asObservable();

	private sceDisconnect = new Subject<any>();
	public readonly sceDisconnect$ = this.sceDisconnect.asObservable();

	private _connectedCodes = new Set<number>();
	private readonly _required_connectedCodes = [24, 28, 31, 32];

	private log(message: string) {
    	console.warn('[SCE]', message);
    	this._sce_log$.next(message);
  	}

  	constructor(private svcVersion : VersionsService,private svcPlatform : PlatformService) {}

	async nextEvent(encodedEvent: string): Promise<void> {
		try {
			const statusSCE : StatusSECPlugin = await CapacitorPluginSce.getStatus();
			const decoded = JSON.parse(atob(encodedEvent));
			console.log('Event reçu :', decoded);
			this._sce_event$.next(decoded);
	
			switch (decoded.code) {
				case 11:
					this.log(`${decoded.code} : La détection des lecteurs a commencé.`);
					this._sceStatus$.next({
						status : 'waiting-lecteur',
						message : "La connexion au lecteur déja établie."
						});
					break;
				case 12:
					this.log(`${decoded.code} : La détection des lecteurs s\'est terminée.`);
					if (ConfigData.lecteurConnected && statusSCE.reader_connected)
					{
						this._sceStatus$.next({
							status : 'ready',
							message : "La connexion au lecteur déja établie."
							});
					}
					break;
				case 13:
					this.log(`${decoded.code} : Un lecteur a été détecté : ${decoded.msg}`);
					this._sceStatus$.next({
						status: 'lecteur-detected',
						message: decoded.msg
					});
					break;
				case 14:
					this.log(`${decoded.code} : La connexion au lecteur Bluetooth a été établie : ${decoded.msg}`);
					ConfigData.lecteurConnected = true;
					this._sceStatus$.next({
						status : 'ready',
						message : "La connexion au lecteur Bluetooth a été établie."
						});
					break;
				case 15:
					this.log(`${decoded.code} : Le lecteur Bluetooth a été déconnecté.`);
					ConfigData.lecteurConnected = false;
					this._sceStatus$.next({
						status : 'error',
						message : `${decoded.code} - Le lecteur Bluetooth a été déconnecté.`
						});
					break;
				case 21:
					this.log(`${decoded.code} : Erreur d\'initialisation du SCEClient`);
					this._sceStatus$.next({
						status : 'error',
						message : `${decoded.code} - Erreur d\'initialisation du SCEClient`
						});
					break;
				case 22:
					this.log(`${decoded.code} : SCEClient initialisé avec succès.`);
					this._sceStatus$.next({
						status : 'initializing',
						message : "SCEClient initialisé avec succès."
						});
					break;
				case 23:
					this.log(`${decoded.code} : Device non enregistré. Un deviceId doit être renseigné.`);
					this._sceStatus$.next({
						status : 'waiting-deviceId',
						message : "Device non enregistré. Un deviceId doit être renseigné."
						});
					break;
				case 24:
					this.log(`${decoded.code} : Device enregistré.`);
					this._trackConnectedProgress(24);

					break;
				case 25:
					this.log(`${decoded.code} : Erreur de renouvellement du certificat.`);
					this._sceStatus$.next({
						status : 'error',
						message : `${decoded.code} - Erreur de renouvellement du certificat.`
						});
					break;
				case 26:
					this.log(`${decoded.code} : Erreur de récupération des détails de l\'application.`);
					this._sceStatus$.next({
						status : 'error',
						message : `${decoded.code} - Erreur de récupération des détails de l\'application.`
						});
					break;
				case 28:
					this.log(`${decoded.code} : Device appairé.`);
					this._trackConnectedProgress(28);
					break;
				case 29:
					this.log(`${decoded.code} : Erreur lors de la connexion au SCE.`);
					this._sceStatus$.next({
						status : 'error',
						message : `${decoded.code} - Erreur lors de la connexion au SCE.`
						});
					break;
				case 30:
					this.log(`${decoded.code} : Erreur lors de l’évènement.`);
					this._sceStatus$.next({
						status : 'error',
						message : `${decoded.code} - Erreur lors de l’évènement.`
						});
					break;
				case 31:
					this.log(`${decoded.code} : SCEProc initialisé avec succès.`);
					this._trackConnectedProgress(31);
					break;
				case 32:
					this.log(`${decoded.code} : Connexion au SCEProc établie avec succès.`);
					this._trackConnectedProgress(32);
					break;
				case 33:
					this.log(`${decoded.code} : Le SCE est déconnecté.`);
					this.sceDisconnect.next({
						status : 'disconnect',
						message : "SCE déconnecté"
					})
					break;
				case 41:
					this.log(`${decoded.code} : Code porteur demandé.`);
					this._sceStatus$.next({
						status : 'waiting-code',
						message : " Code porteur demandé."
						});
					break;
				case 42:
					this.log(`${decoded.code} : Code porteur incorrect (restant 1 ou 2 essais).`);
					this._sceStatus$.next({
						status : 'error',
						message : `${decoded.code} - Code porteur incorrect (restant 1 ou 2 essais).`
						});
					break;
				case 43:
					this.log(`${decoded.code} : Code porteur valide.`);
					this._sceStatus$.next({
						status : 'ready-code',
						message : "Code porteur valide."
						});
					break;
				case 51:
					this.log(`${decoded.code} : Une carte CPx a été insérée.`);
					break;
				case 52:
					this.log(`${decoded.code} : Une carte CPx a été arrachée.`);
					break;
				case 53:
					this.log(`${decoded.code} : Une carte Vitale a été insérée.`);
					break;
				case 54:
					this.log(`${decoded.code} : Une carte Vitale a été arrachée.`);
					break;
	
				default:
					this.log(`${decoded.code} : Code inconnu reçu :  ${decoded.code} - ${decoded.msg}`);
					break;
			}
		} catch (err) {
			console.error('Erreur de décodage d\'event', err);
			this._sceStatus$.next({
				status : 'error',
				message : "Erreur de décodage d\'event."
				});
		}
	}

	async initialize() {
		const deviceTypeUser: EDeviceType = this.svcPlatform.getDeviceType();
		try {
			await CapacitorPluginSce.initialize({
				appVersion: `DESMOSInfirmiers/${deviceTypeUser}/${ConfigData.environment.id}/${this.svcVersion.version}`,
				env : `${ConfigData.environment.id}`
			});
			console.warn('Initialisation complète OK');
			ConfigData.sceInitialize = true;
			CapacitorPluginSce.addListener('onEvent', (data: { event: string }) => {
				this.nextEvent(data.event);
			});
		} catch (e) {
			console.error('Erreur dans initializeSce', e);
		}
	}

  	async disconnect() {
    	try {
      	await CapacitorPluginSce.disconnect();
    	} catch (err) {
      	console.error('Erreur lors de la déconnexion du SCE', err);
    	}
  	}

	public emitStatus(status: SceStatusType): void {
		console.warn(`[SCE SERVICE] Statut émis manuellement : ${status}`);
		this._sceStatus$.next(
			{
				status : status,
			message :  `Statut émis manuellement : ${status}`
			});
	}

	private _trackConnectedProgress(code: number) {
		if (this._required_connectedCodes.includes(code)) {
			this._connectedCodes.add(code);
			console.log(`[SCE TRACK] Code ${code} reçu. Progression: [${[...this._connectedCodes].join(', ')}]`);
	
			if (this._required_connectedCodes.every(c => this._connectedCodes.has(c))) {
				this._sceStatus$.next({
					status: 'connected',
					message: 'Tous les prérequis reçus : lecteur prêt.',
				});
				this._connectedCodes.clear();
			}
		}
	}
	
}
